import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Download, Loader2, X } from "lucide-react";
import { useStageContainer } from "@/components/game/Stage";
import {
  RULEBOOK_SECTIONS,
  RULEBOOK_PAGE_W,
  RULEBOOK_PAGE_H,
  RULEBOOK_PDF_ENABLED,
  RULEBOOK_PDF_URL,
  RULEBOOK_PDF_NAME,
  rulebookPageAlt,
} from "@/lib/rulebook";
import { RULEBOOK_PAGE_CONTENT } from "@/components/game/RulebookPages";

const STORE_KEY = "porosity.rulebook.position";
const PAGE_GAP = 24;
const NARROW_BELOW = 900;
const PAGE_SHADOW = "0 1px 2px rgba(42,46,44,.08), 0 8px 24px rgba(42,46,44,.12)";

const isNarrow = () => typeof window !== "undefined" && window.innerWidth < NARROW_BELOW;

function useNarrow() {
  const [narrow, setNarrow] = useState(isNarrow);
  useEffect(() => {
    const on = () => setNarrow(isNarrow());
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  return narrow;
}

function readSaved() {
  try {
    const v = JSON.parse(sessionStorage.getItem(STORE_KEY) || "null");
    if (v && Number.isInteger(v.page) && v.page >= 0 && v.page < RULEBOOK_SECTIONS.length) return v;
  } catch {
    /* ignore */
  }
  return null;
}

function writeSaved(page, frac) {
  try {
    sessionStorage.setItem(STORE_KEY, JSON.stringify({ page, frac: Math.max(0, Math.min(1, frac)) }));
  } catch {
    /* ignore */
  }
}

// One v2 text page per section. Same frame as the printed pages: the page keeps the
// printed aspect ratio as its minimum height and grows when the text needs more room.
function RulebookPage({ section, index, pageRef }) {
  const Content = RULEBOOK_PAGE_CONTENT[section.key];
  return (
    <section
      ref={pageRef}
      data-page-index={index}
      data-testid={`rulebook-page-${section.page}`}
      aria-label={rulebookPageAlt(section)}
      className="relative w-full mx-auto"
      style={{
        maxWidth: RULEBOOK_PAGE_W,
        aspectRatio: `${RULEBOOK_PAGE_W} / ${RULEBOOK_PAGE_H}`,
        background: "var(--card)",
        borderRadius: 4,
        boxShadow: PAGE_SHADOW,
        padding: "40px 44px 48px",
      }}
    >
      <div className="flex items-baseline gap-3" style={{ borderBottom: "1px solid var(--line)", paddingBottom: 12, marginBottom: 16 }}>
        <span style={{ fontFamily: "var(--font-mono)", fontSize: 13, color: "var(--sea)", fontWeight: 600 }}>{String(section.page).padStart(2, "0")}</span>
        <h3 data-testid={`rulebook-page-title-${section.page}`} style={{ fontSize: 26, fontWeight: 700, color: "var(--ink)", lineHeight: 1.2 }}>
          {section.title}
        </h3>
        <span className="ml-auto" style={{ fontSize: 12, color: "var(--ink-5)", letterSpacing: ".06em", textTransform: "uppercase" }}>Porosity rules</span>
      </div>
      {Content ? <Content /> : null}
    </section>
  );
}

function RulebookPanel({ onClose, inStage = false }) {
  // Inside the game stage the layout never switches (the stage only scales); the home page keeps the narrow view.
  const narrow = useNarrow() && !inStage;
  const scrollRef = useRef(null);
  const pageEls = useRef([]);
  const jumpRef = useRef(null);
  const rafRef = useRef(0);
  const pressedBackdrop = useRef(false);
  const [active, setActive] = useState(() => readSaved()?.page ?? 0);
  const [downloading, setDownloading] = useState(false);

  // Esc closes. Capture phase + stop, so the game's own Esc (deselect card) never sees it.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      onClose();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onClose]);

  // Focus the pages so arrow keys / PageDown scroll them; give focus back on close.
  useEffect(() => {
    const prev = document.activeElement;
    scrollRef.current?.focus({ preventScroll: true });
    return () => {
      if (prev && typeof prev.focus === "function") prev.focus({ preventScroll: true });
    };
  }, []);

  // Restore the remembered position for this session (cover on first open).
  useLayoutEffect(() => {
    const el = scrollRef.current;
    const saved = readSaved();
    if (!el || !saved) return;
    const p = pageEls.current[saved.page];
    if (p) el.scrollTop = p.offsetTop - PAGE_GAP + saved.frac * p.offsetHeight;
  }, []);

  const measure = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const top = el.scrollTop;
    const probe = top + el.clientHeight * 0.35;
    const atBottom = top + el.clientHeight >= el.scrollHeight - 2;
    let current = 0;
    pageEls.current.forEach((p, i) => {
      if (p && p.offsetTop <= probe) current = i;
    });
    if (atBottom) current = RULEBOOK_SECTIONS.length - 1;

    // Remember position: the page at the top edge + how far into it.
    let topIdx = 0;
    pageEls.current.forEach((p, i) => {
      if (p && p.offsetTop - PAGE_GAP <= top) topIdx = i;
    });
    const tp = pageEls.current[topIdx];
    if (tp) writeSaved(topIdx, (top - (tp.offsetTop - PAGE_GAP)) / Math.max(1, tp.offsetHeight));

    const jump = jumpRef.current;
    if (jump) {
      if (Date.now() < jump.until && Math.abs(top - jump.target) > 2) return;
      jumpRef.current = null;
    }
    setActive(current);
  }, []);

  const onScroll = () => {
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(measure);
  };
  useEffect(() => () => cancelAnimationFrame(rafRef.current), []);

  const jumpTo = (i) => {
    const el = scrollRef.current;
    const p = pageEls.current[i];
    if (!el || !p) return;
    const target = Math.max(0, Math.min(p.offsetTop - PAGE_GAP, el.scrollHeight - el.clientHeight));
    jumpRef.current = { target, until: Date.now() + 1500 };
    setActive(i);
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    el.scrollTo({ top: target, behavior: reduce ? "auto" : "smooth" });
  };

  const onDownload = async (e) => {
    e.preventDefault();
    if (downloading) return;
    setDownloading(true);
    try {
      const res = await fetch(RULEBOOK_PDF_URL);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = RULEBOOK_PDF_NAME;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch {
      window.open(RULEBOOK_PDF_URL, "_blank", "noopener");
    } finally {
      setDownloading(false);
    }
  };

  // Keep every click / key inside the overlay from reaching the game underneath.
  const stop = (e) => e.stopPropagation();

  return (
    <div
      data-testid="rulebook-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="rulebook-title"
      className="fixed inset-0 z-[90] flex items-center justify-center"
      style={{ background: "rgba(42,46,44,.45)" }}
      onPointerDown={(e) => {
        pressedBackdrop.current = e.target === e.currentTarget;
        stop(e);
      }}
      onClick={(e) => {
        stop(e);
        if (e.target === e.currentTarget && pressedBackdrop.current) onClose();
        pressedBackdrop.current = false;
      }}
      onMouseDown={stop}
      onKeyDown={stop}
      onWheel={stop}
      onContextMenu={stop}
    >
      <div
        data-testid="rulebook-panel"
        className="flex flex-col overflow-hidden"
        style={{
          width: narrow ? "calc(100% - 24px)" : "min(1120px, calc(100% - 48px))",
          height: "85%",
          background: "var(--card)",
          borderRadius: 16,
          border: "1px solid var(--line)",
          boxShadow: "0 2px 4px rgba(42,46,44,.08), 0 24px 64px rgba(42,46,44,.22)",
        }}
      >
        <div
          data-testid="rulebook-topbar"
          className="shrink-0 flex items-center gap-2"
          style={{ height: 60, padding: narrow ? "0 8px 0 16px" : "0 12px 0 24px", borderBottom: "1px solid var(--line)" }}
        >
          <h2 id="rulebook-title" data-testid="rulebook-title" style={{ fontSize: 18, fontWeight: 700, color: "var(--ink)" }}>
            Rulebook
          </h2>
          <div className="ml-auto flex items-center gap-1">
            {RULEBOOK_PDF_ENABLED && (
              <a
                href={RULEBOOK_PDF_URL}
                download={RULEBOOK_PDF_NAME}
                onClick={onDownload}
                className="btn-ghost"
                data-testid="rulebook-download-btn"
                aria-busy={downloading}
              >
                {downloading ? <Loader2 size={16} strokeWidth={1.8} className="animate-spin" /> : <Download size={16} strokeWidth={1.8} />}
                {downloading ? "Downloading…" : "Download PDF"}
              </a>
            )}
            <button type="button" onClick={onClose} className="btn-ghost" aria-label="Close rulebook" data-testid="rulebook-close-btn">
              <X size={18} strokeWidth={1.8} />
            </button>
          </div>
        </div>

        {narrow && (
          <div className="shrink-0 flex items-center gap-3" style={{ padding: "10px 16px", borderBottom: "1px solid var(--line)" }}>
            <label htmlFor="rulebook-sections" style={{ fontSize: 13, fontWeight: 600, color: "var(--ink-3)" }}>
              Sections
            </label>
            <select
              id="rulebook-sections"
              data-testid="rulebook-sections-select"
              value={active}
              onChange={(e) => jumpTo(Number(e.target.value))}
              className="flex-1 min-w-0 h-11 px-3"
              style={{ fontSize: 14, color: "var(--ink)", background: "var(--surface)", border: "1px solid var(--line)", borderRadius: 8 }}
            >
              {RULEBOOK_SECTIONS.map((s, i) => (
                <option key={s.page} value={i}>
                  {s.page}. {s.title}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="flex-1 min-h-0 flex">
          {!narrow && (
            <nav
              data-testid="rulebook-toc"
              aria-label="Rulebook contents"
              className="shrink-0 overflow-y-auto"
              style={{ width: 248, padding: "16px 12px", borderRight: "1px solid var(--line)" }}
            >
              <div className="section-label" style={{ fontSize: 11, padding: "0 12px 8px", color: "var(--ink-5)", letterSpacing: ".08em", textTransform: "uppercase", fontWeight: 600 }}>
                Contents
              </div>
              <ol className="flex flex-col gap-0.5">
                {RULEBOOK_SECTIONS.map((s, i) => {
                  const on = i === active;
                  return (
                    <li key={s.page}>
                      <button
                        type="button"
                        onClick={() => jumpTo(i)}
                        data-testid={`rulebook-toc-item-${s.page}`}
                        aria-label={`${s.title}, page ${s.page}`}
                        data-active={on ? "true" : "false"}
                        aria-current={on ? "true" : undefined}
                        className="w-full flex items-center justify-between gap-3 text-left"
                        style={{
                          minHeight: 36,
                          padding: "6px 12px",
                          borderRadius: 8,
                          fontSize: 14,
                          lineHeight: 1.3,
                          fontWeight: on ? 600 : 400,
                          color: on ? "var(--sea)" : "var(--ink-2)",
                          background: on ? "rgba(36,73,90,.08)" : "transparent",
                          boxShadow: on ? "inset 3px 0 0 var(--sea)" : "none",
                          transition: "background 150ms ease, color 150ms ease",
                        }}
                      >
                        <span>{s.title}</span>
                        <span style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: on ? "var(--sea)" : "var(--ink-5)" }}>{s.page}</span>
                      </button>
                    </li>
                  );
                })}
              </ol>
            </nav>
          )}

          <div
            ref={scrollRef}
            data-testid="rulebook-pages"
            tabIndex={-1}
            onScroll={onScroll}
            className="relative flex-1 min-w-0 overflow-y-auto outline-none"
            style={{ background: "var(--surface)", overscrollBehavior: "contain" }}
          >
            <div className="flex flex-col" style={{ gap: PAGE_GAP, padding: narrow ? `${PAGE_GAP}px 12px` : PAGE_GAP }}>
              {RULEBOOK_SECTIONS.map((s, i) => (
                <RulebookPage
                  key={s.page}
                  section={s}
                  index={i}
                  pageRef={(el) => {
                    pageEls.current[i] = el;
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// One shared Rulebook view. In a game it portals into the stage so it scales with it;
// on the home page it sits over the viewport. Opening it never touches game state.
export default function Rulebook({ open, onClose }) {
  const container = useStageContainer();
  if (!open) return null;
  return createPortal(<RulebookPanel onClose={onClose} inStage={!!container} />, container || document.body);
}
